import {Controller, useFormContext} from "react-hook-form";
import type {HTMLAttributes} from "react";
import {Input, Space, Radio} from "antd";
import {LiveEventLocationFormFieldType} from "./types";
import {FormFieldError} from "../../../../design-system/form-field";
import {LocationSelect} from "../../location-select";
import {useGoogleCalendarConnection} from "../../../../react-query-hooks/connect-calendar/useGoogleCalendarConnection";
import {useConnectGoogleCalendarAndResume} from "../../../../utils/google-calendar-resume";
import {IS_PHYSICAL_LOCATION_FEATURE_ENABLED} from "../../../../services/config";
import {ZoomIcon} from "../../../../pages/dashboard/automations/icons/zoom-icon";
import {useGetIntegrationsStatus} from "../../../../react-query-hooks/integrations/integrations";
import {PhysicalLocationFields} from "./physical-location-fields";

type LocationSelectValue = Exclude<
  LiveEventLocationFormFieldType["callLocation"],
  "zoom"
>;

const StyledCallLocationItem = ({
  active,
  ...props
}: {active?: boolean} & HTMLAttributes<HTMLDivElement>) => <div {...props} />;

export function LiveEventLocationFormFields() {
  const {control, watch, clearErrors, setValue} =
    useFormContext<LiveEventLocationFormFieldType>();
  const integrationsStatusQuery = useGetIntegrationsStatus();
  const {isConnected} = useGoogleCalendarConnection();
  const {connectAndResume, isConnecting} = useConnectGoogleCalendarAndResume();

  const callLocation = watch("callLocation");
  const isCustomUrlSelected = callLocation === "custom_url";
  const isPhysicalLocationSelected =
    IS_PHYSICAL_LOCATION_FEATURE_ENABLED && callLocation === "physical_location";
  const isZoomSelected = watch("callLocation") === "zoom";
  // Show the Zoom option to coaches who have connected Zoom. Also keep it
  // visible if it's already selected (e.g. editing a Zoom event), even if the
  // status hasn't loaded yet or Zoom was later disconnected.
  const showZoomOption =
    Boolean(integrationsStatusQuery.data?.zoomConnected) || isZoomSelected;

  return (
    <div>
      <Controller
        control={control}
        name="callLocation"
        render={({field: {onChange, value}}) => (
          <LocationSelect
            value={value as LocationSelectValue}
            onChange={(newValue) => {
              clearErrors(["callLocation", "customUrl", "locationAddress"]);
              onChange(newValue);
            }}
            calendarConnected={isConnected}
            onConnectCalendar={connectAndResume}
            isConnectingCalendar={isConnecting}
            variant="live-event"
          />
        )}
      />
      {isCustomUrlSelected && (
        <div style={{marginTop: "1.2rem"}}>
          <Controller
            control={control}
            name="customUrl"
            render={({field: {onChange, value, ref, onBlur}, fieldState: {error}}) => (
              <Space size={8} direction="vertical" style={{display: "flex"}}>
                <label style={{fontSize: "1.4rem", color: "#555"}}>
                  Add a custom link
                </label>
                <Input
                  status={error?.message ? "error" : ""}
                  onChange={onChange}
                  onBlur={onBlur}
                  size="large"
                  value={value === null ? undefined : value}
                  placeholder="Paste a valid URL"
                  ref={ref}
                />
                <FormFieldError errorMessage={error?.message} />
              </Space>
            )}
          />
        </div>
      )}
      {isPhysicalLocationSelected && (
        <div style={{marginTop: "1.2rem"}}>
          <PhysicalLocationFields />
        </div>
      )}
      {showZoomOption && (
        <div style={{marginTop: "1.2rem"}}>
          <StyledCallLocationItem
            style={{backgroundColor: "#fff"}}
            onClick={() => {
              clearErrors(["callLocation", "customUrl"]);
              setValue(
                "callLocation",
                "zoom" as LiveEventLocationFormFieldType["callLocation"]
              );
            }}
            active={isZoomSelected}
          >
            <div className="icon-container">
              <ZoomIcon />
            </div>
            <div className="location-info">
              <h3 className="title">Zoom</h3>
              <p className="sub-info">
                A unique Zoom meeting link is generated automatically for every booking
                using your connected Zoom account
              </p>
            </div>
            <div style={{marginLeft: "auto"}}>
              <Radio value={"zoom"} />
            </div>
          </StyledCallLocationItem>
        </div>
      )}
    </div>
  );
}
